import { CardContent } from "@/components/ui/card";
import { useState, useEffect, useRef } from "react";
import { ProfileAvatar } from "./header/ProfileAvatar";
import { EnterpriseProfileInfo } from "./EnterpriseProfileInfo";
import { EnterpriseProfileStats } from "./EnterpriseProfileStats";
import { ProfileActions } from "./header/ProfileActions";
import { toast } from "@/components/ui/use-toast";
import imagePlaceholder from "@/assets/images/image.png";

interface ContactPerson {
  name: string;
  email: string;
  phone: string;
}

interface Location {
  address: string;
  city: string;
  country: string;
}

interface PreviousProject {
  projectName: string;
  description: string;
  budget: number;
  completedOn: string;
  _id: string;
}

interface EnterpriseProfileHeaderProps {
  isEditing: boolean;
  formData: {
    companyName: string;
    industryType: string;
    logo: string;
    contactPerson: ContactPerson;
    location: Location;
    preferredCurrency: string;
    preferredPaymentMethod: string;
    vatOrGstId: string;
    socialMediaProfile: string;
    reviews: object;
    previousProjects: PreviousProject[];
    avatarUrl?: string;
  };
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  setIsEditing: (value: boolean) => void;
  setFormData?: (data: any) => void;
  updateEnterpriseProfile: (
    id: string,
    data: Partial<any>,
    token: string
  ) => Promise<void>;
uploadEnterpriseProfilePicture: (
  file: File,
  token: string,
  userId: string,
  uploadType?: string
) => Promise<any>;
  accessToken: string | null;
  userId: string;
  toast: (options: Parameters<typeof toast>[0]) => void;
  handleCancel?: () => void;
  isClientProfile?: boolean
}

const EnterpriseProfileHeader = ({
  isEditing,
  formData,
  handleInputChange,
  setIsEditing,
  setFormData,
  updateEnterpriseProfile,
  uploadEnterpriseProfilePicture,
  accessToken,
  userId,
  toast,
  handleCancel,
  isClientProfile
}: EnterpriseProfileHeaderProps) => {
  const [avatarPreview, setAvatarPreview] = useState<string>(
    formData.logo || imagePlaceholder);
  const fileInputRef = useRef<HTMLInputElement>(null);
 
  // Sync avatar preview
useEffect(() => {
  if (formData.logo && !avatarPreview.startsWith("blob:")) {
    setAvatarPreview(formData.logo);
  }
}, [formData.logo]);

  const handleAvatarClick = () => {
    if (isEditing && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

const handleAvatarChange = async (previewUrl: string, file: File) => {

  if (avatarPreview && avatarPreview.startsWith("blob:")) {
    URL.revokeObjectURL(avatarPreview);
  }

  setAvatarPreview(previewUrl);

  try {
    const res = await uploadEnterpriseProfilePicture(file, accessToken, userId);

    // Use backend URL once it's available
    const newImageUrl = res.data?.profilePicture;
    if (newImageUrl) {
      setAvatarPreview(newImageUrl); // update from backend
      setFormData?.((prev: any) => ({
        ...prev,
        logo: newImageUrl, // store in logo field
      }));
    }

    toast({
      title: "Profile Picture Updated",
      description: "Your enterprise profile picture has been successfully uploaded.",
      variant: "success",
    });
  } catch (error) {
    toast({
      title: "Error",
      description: "Failed to upload profile picture. Please try again.",
      variant: "destructive",
    });
  }

  // Reset file input to allow re-uploading same file
  if (fileInputRef.current) {
    fileInputRef.current.value = '';
  }
};


const handleUploadProfilePicture = async (file: File) => {
  try {
    await uploadEnterpriseProfilePicture(file, accessToken, userId);
    toast({
      title: "Profile Picture Updated",
      description: "Your enterprise profile picture has been successfully uploaded.",
      variant: "success",
    });
  } catch (error) {
    toast({
      title: "Error",
      description: "Failed to upload profile picture. Please try again.",
      variant: "destructive",
    });
  }
};

  const handleSave = async () => {
    if (!accessToken || !userId) {
      toast({
        title: "Error",
        description: "You must be logged in to update your profile.",
        variant: "destructive",
      });
      return;
    }
    // Social media validation
    if (formData?.socialMediaProfile) {
      const linkedinRegex = /^https:\/\/(www\.)?linkedin\.com/;
      if (!linkedinRegex.test(formData.socialMediaProfile)) {
        toast({
          title: "Invalid Link",
          description: "Social media link must start with https://www.linkedin.com or https://linkedin.com",
          variant: "destructive",
        });
        return; // Stop save if invalid
      }
    }

    const updatedData = {
      companyName: formData?.companyName,
      industryType: formData?.industryType,
      contactPerson: formData?.contactPerson,
      location: formData?.location,
      preferredCurrency: formData?.preferredCurrency,
      preferredPaymentMethod: formData?.preferredPaymentMethod,
      vatOrGstId: formData?.vatOrGstId,
      socialMediaProfile: formData?.socialMediaProfile,
      reviews: formData?.reviews,
    };

    try {
      await updateEnterpriseProfile(userId, updatedData, accessToken);
      setIsEditing(false);
      toast({
        title: "Profile Updated",
        description: "Your enterprise profile has been successfully updated.",
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  const checkFormValidity = () => {
    return formData.companyName.trim() !== "";
  };

  return (
    <CardContent className="pt-6">
      <ProfileAvatar
        avatarPreview={avatarPreview}
        isEditing={isEditing}
        localAvailability="enterprise"
        handleAvatarClick={handleAvatarClick}
        fileInputRef={fileInputRef}
        onAvatarChange={handleAvatarChange}
      />

      <EnterpriseProfileInfo
        isEditing={isEditing}
        formData={formData}
        handleInputChange={handleInputChange}
      />

      <EnterpriseProfileStats
        reviews={formData.reviews}
        projectCount={formData.previousProjects.length}
      />

      {!isClientProfile &&<ProfileActions
        isEditing={isEditing}
        checkFormValidity={checkFormValidity}
        handleSave={handleSave}
        setIsEditing={setIsEditing}
        handleCancel={handleCancel}
      />}
    </CardContent>
  );
};

export default EnterpriseProfileHeader;
